/**
 * HALO - Organization Setup Page
 * Clean onboarding for new admins
 * Design: Centered card with clear hierarchy
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Users,
} from "lucide-react";
import { createOrganizationWithAdmin, supabase } from "../lib/supabase";

interface OrganizationSetupProps {
  userId: string;
  email: string;
  fullName: string;
  onComplete: () => void;
}

export default function OrganizationSetup({
  userId,
  email,
  fullName,
  onComplete,
}: OrganizationSetupProps) {
  const [organizationName, setOrganizationName] = useState("");
  const [displayName, setDisplayName] = useState(fullName);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await createOrganizationWithAdmin(
        userId,
        email,
        displayName || fullName,
        organizationName,
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to create organization");
      }

      onComplete();
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-halo px-4 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-20 pointer-events-none" />

      {/* Ambient glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/20 rounded-full blur-3xl pointer-events-none" />

      <motion.div
        className="w-full max-w-md relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <motion.div
            className="inline-flex items-center justify-center mb-6"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
          >
            <img src="/haloai_logo.webp" alt="HALO Logo" className="w-16 h-16 object-contain" />
          </motion.div>
          <motion.h1
            className="text-3xl font-semibold text-foreground tracking-tight mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Set Up Organization
          </motion.h1>
          <motion.p
            className="text-muted-slate"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
          >
            Create your team's wellness workspace
          </motion.p>
        </div>

        {/* Setup Card */}
        <motion.div
          className="halo-card"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          {/* Progress Indicator */}
          <div className="flex items-center gap-2 mb-6">
            <div className="halo-badge halo-badge--blue">
              <span className="w-1.5 h-1.5 rounded-full bg-signal-blue" />
              Step 1 of 1
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              className="mb-4 p-3 rounded-xl bg-signal-crimson/10 border border-signal-crimson/20 flex items-start gap-3"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
            >
              <AlertCircle className="w-5 h-5 text-signal-crimson flex-shrink-0 mt-0.5" />
              <p className="text-sm text-signal-crimson">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-slate mb-2">
                Organization Name *
              </label>
              <input
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                required
                className="halo-input"
                placeholder="Acme Corporation"
              />
              <p className="text-xs text-muted-slate mt-1.5">
                This is your company or team name
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-slate mb-2">
                Your Name
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="halo-input"
                placeholder="John Doe"
              />
            </div>

            {/* Admin Features Preview */}
            <motion.div
              className="bg-surface-dark border border-glass-grey rounded-xl p-4 mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <p className="text-sm font-medium text-primary mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                As admin, you'll be able to:
              </p>
              <ul className="space-y-2">
                {[
                  "Create employee accounts with credentials",
                  "View team wellness data in real-time",
                  "Manage employee roles and access",
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    className="flex items-start gap-2 text-sm text-muted-slate"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                  >
                    <CheckCircle2 className="w-4 h-4 text-signal-green flex-shrink-0 mt-0.5" />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.button
              type="submit"
              disabled={loading || !organizationName.trim()}
              className="w-full halo-btn halo-btn--primary mt-6"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Creating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Create Organization
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </motion.button>
          </form>

          {/* Sign Out Option */}
          <div className="mt-6 pt-6 border-t border-glass-grey">
            <p className="text-center text-sm text-muted-slate mb-3">
              Signed in as <span className="text-foreground">{email}</span>
            </p>
            <button
              onClick={handleSignOut}
              className="w-full text-center text-sm text-muted-slate hover:text-foreground transition-colors duration-micro"
            >
              Sign out and use a different account
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
